import { useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useAuthContext } from '../../../common/contexts/useAuthContext';
import { userConfigState } from '../atoms/userConfigState';
import { playerTeamDataSelector } from '../selectors/playerTeamDataSelector';
import { playersState } from '../atoms/playersState';
import { playerOverviewsState } from '../atoms/playerOverviewsState';
import { playerCareersState } from '../atoms/playerCareersState';
import { playerDataLastRefreshState } from '../atoms/playerDataLastRefreshState';
import { getPlayerOverviews } from '../../services/server/application/playerOverviews';
import { getPlayerCareers } from '../../services/server/application/playerCareers';


export const useSyncPlayerData = () => {

  const { currentUser } = useAuthContext();
  const userConfig = useRecoilValue(userConfigState);

  const playerTeamData = useRecoilValue(playerTeamDataSelector);
  const players = useRecoilValue(playersState);

  const [playerOverviews, setPlayerOverviews] = useRecoilState(playerOverviewsState);
  const [playerCareers, setPlayerCareers] = useRecoilState(playerCareersState);

  const [playerDataLastRefresh, setPlayerDataLastRefresh] = useRecoilState(playerDataLastRefreshState);


  useEffect(() => {

    const syncPlayerData = async () => {

      if (!currentUser || !playerTeamData || !players) return;

      const playerIdsToFetchOverviews = new Set<number>();
      const playerIdsToFetchCareers = new Set<number>();

      // we want to fetch player overviews for all players that are in a team and not already in playerOverviews
      Object.keys(playerTeamData).forEach(playerId => {
        const numberId = Number(playerId);
        if (!isNaN(numberId)) {
          if (!(playerId in playerOverviews)) {
            playerIdsToFetchOverviews.add(numberId);
          }
          if (!(playerId in playerCareers) && playerTeamData[playerId]['ownTeam']) {
            playerIdsToFetchCareers.add(numberId);
          }
        }
      });

      // we also want to fetch player overviews for all players that are in the academy (if the user has access to it)
      if (userConfig?.accessConfig.hasAccessToAcademy) {
        Object.keys(players).forEach(playerId => {
          const player = players[playerId];
          const numberId = Number(playerId);
          if (player.isAcademyPlayer && !isNaN(numberId) && !(playerId in playerOverviews)) {
            playerIdsToFetchOverviews.add(numberId);
          }
        });
      }

      if (playerIdsToFetchOverviews.size > 0) {
        const newPlayerOverviews = await getPlayerOverviews(Array.from(playerIdsToFetchOverviews), currentUser);
        if (newPlayerOverviews) {
          setPlayerOverviews(currentPlayerOverviews => ({ ...currentPlayerOverviews, ...newPlayerOverviews }));
        }
      }

      if (playerIdsToFetchCareers.size > 0) {
        const newPlayerCareer = await getPlayerCareers(Array.from(playerIdsToFetchCareers), currentUser);
        if (newPlayerCareer) {
          setPlayerCareers(currentPlayerCareers => ({ ...currentPlayerCareers, ...newPlayerCareer }));
        }
      }

      if (playerDataLastRefresh === null) {
        setPlayerDataLastRefresh(new Date().toISOString().split('T')[0]);
      }
    };

    syncPlayerData();

  }, [currentUser, playerTeamData, players, userConfig?.accessConfig.hasAccessToAcademy]); // eslint-disable-line react-hooks/exhaustive-deps

};
